import {
  formatCalendarDate,
  HEALTHY_SUBSCRIPTION_STATUSES,
  possessive,
  resolveDateFormat,
} from '@snootsvet/shared/portal'

import { useGetMe } from '@/customer/hooks/use-get-me'
import { useListInvoices } from '@/customer/hooks/use-list-invoices'
import { usePetsWithSubscriptions } from '@/customer/hooks/use-pets-with-subscriptions'
import { useRegion } from '@/customer/hooks/use-region'
import {
  hasOnboardedPet,
  type MembershipIssue,
  membershipStartsOn,
} from '@/customer/lib/membership'
import { routes } from '@/customer/routes'

export function useMembershipIssues(): { issues: MembershipIssue[]; isPending: boolean } {
  const me = useGetMe()
  const { pets, isPending: petsPending } = usePetsWithSubscriptions()
  const { invoices, isPending: invoicesPending } = useListInvoices({ status: 'overdue' })
  const region = useRegion()

  if (
    me.isPending ||
    !me.data ||
    petsPending ||
    invoicesPending ||
    pets.some((pet) => pet.subscriptionQuery.isPending)
  ) {
    return { issues: [], isPending: true }
  }

  const issues: MembershipIssue[] = []

  if (invoices.length > 0) {
    issues.push({
      type: 'overdue_invoice',
      title: 'You have overdue invoices',
      action: { href: routes.account.billing.index, label: 'View invoices' },
    })
  }

  const hasCompletedPetOnboarding = hasOnboardedPet(me.data.patients)
  const hasBookablePet = pets.some((pet) => {
    const status = pet.subscriptionQuery.data?.subscription?.status
    return status && HEALTHY_SUBSCRIPTION_STATUSES.has(status)
  })

  if (hasCompletedPetOnboarding && !hasBookablePet) {
    const [scheduled] = pets.flatMap((pet) => {
      const startsOn = membershipStartsOn(pet.subscriptionQuery.data?.subscription)
      return startsOn ? [{ name: pet.name, startsOn }] : []
    })
    issues.push(
      scheduled
        ? {
            type: 'no_bookable_pet',
            title: `${possessive(scheduled.name)} membership starts on ${formatCalendarDate(
              scheduled.startsOn,
              resolveDateFormat(null, region.id),
              'long'
            )}`,
            action: { href: routes.account.pets.index, label: 'View membership' },
          }
        : {
            type: 'no_bookable_pet',
            title: 'None of your pets currently have an active membership',
            action: { href: routes.account.pets.index, label: 'Activate membership' },
          }
    )
  }

  return { issues, isPending: false }
}
